import React, { useRef, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import GetAppIcon from "@material-ui/icons/GetApp";
import PreviewIcon from "@material-ui/icons/Visibility";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import BlockIcon from "@material-ui/icons/Block";
import { Link } from "react-router-dom";
import Axios from "axios";
import swal from "sweetalert";
import { fade } from "@material-ui/core/styles/colorManipulator";

import DashboardAdmin from "../../DashboardAdmin";
import useUserState from "../../../hooks/useUserState";
import useBDocTranslator from "../../../hooks/useBDocTranslator";
import { extractApiError } from "../../../../services/apiErrorMessage";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#f5f5f5",
    minHeight: "100vh",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  warning: {
    marginBottom: theme.spacing(3),
    "& ul": {
      marginBottom: 0,
    },
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  fields: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginTop: theme.spacing(3),
  },
  field: {
    minWidth: 220,
    flex: "1 1 220px",
  },
  actions: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  table: {
    minWidth: 700,
  },
  tableHeader: {
    backgroundColor: fade(theme.palette.primary.main, 0.05),
    "& .MuiTableCell-head": {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  summary: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.8rem",
  },
  noteText: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
  },
}));

const PLACE_TYPES = [
  { value: "lab", label: "Laboratory" },
  { value: "phar", label: "Pharmacy" },
  { value: "img", label: "Imaging center" },
];

const ON_EXISTING_OPTIONS = [
  { value: "skip", label: "Skip existing places" },
  { value: "update", label: "Update existing places" },
];

// The action the API returns for each row of the plan.
const ACTION_META = {
  create: { color: "primary", icon: CheckCircleIcon },
  update: { color: "secondary", icon: CheckCircleIcon },
  skip: { color: "default", icon: BlockIcon },
  fail: { color: "error", icon: WarningIcon },
};

export default function BulkPlacesImport() {
  const classes = useStyles();
  const { t } = useBDocTranslator();
  const userState = useUserState();
  const hiddenFileInput = useRef(null);

  const [file, setFile] = useState(null);
  const [type, setType] = useState("lab");
  const [agreementCode, setAgreementCode] = useState("10772");
  const [sheet, setSheet] = useState("");
  const [onExisting, setOnExisting] = useState("skip");
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const resetPlan = () => setPlan(null);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    setFile(selected || null);
    resetPlan();
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);
    formData.append("agreement_code", agreementCode || "");
    formData.append("on_existing", onExisting);
    if (sheet) {
      formData.append("sheet", sheet);
    }
    return formData;
  };

  const handlePreview = async () => {
    if (!file) {
      swal(t("Error"), t("Please select an Excel file first."), "error");
      return;
    }

    setLoading(true);
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_API_URL_BASE}/pharmacy/bulk-import/preview`,
        buildFormData(),
        {
          headers: {
            Authorization: "Bearer " + userState[0].jwt,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setPlan(response.data);
    } catch (error) {
      resetPlan();
      swal(
        t("Error"),
        extractApiError(error, t("Could not read the Excel file.")),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!file) {
      swal(t("Error"), t("Please select an Excel file first."), "error");
      return;
    }

    const confirmed = await swal({
      title: t("Confirm the bulk import"),
      text: t(
        "The following places will be written to the database: {{count}}. This operation cannot be undone.",
        { count: plan?.summary?.to_create + plan?.summary?.to_update }
      ),
      icon: "warning",
      buttons: [t("Cancel"), t("Import")],
      dangerMode: true,
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_API_URL_BASE}/pharmacy/bulk-import/execute`,
        buildFormData(),
        {
          headers: {
            Authorization: "Bearer " + userState[0].jwt,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const { created, updated, skipped, failed } = response.data;
      await swal(
        t("Bulk import finished"),
        t(
          "{{created}} created, {{updated}} updated, {{skipped}} skipped, {{failed}} failed.",
          { created, updated, skipped, failed }
        ),
        failed > 0 ? "warning" : "success"
      );

      setFile(null);
      resetPlan();
      if (hiddenFileInput.current) {
        hiddenFileInput.current.value = "";
      }
    } catch (error) {
      swal(
        t("Error"),
        extractApiError(error, t("The bulk import could not be completed.")),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const renderSummaryChips = (summary) => (
    <div className={classes.summary}>
      <Chip size="small" label={`${t("Total")}: ${summary.total}`} />
      <Chip
        size="small"
        color="primary"
        label={`${t("To create")}: ${summary.to_create}`}
      />
      <Chip
        size="small"
        color="secondary"
        label={`${t("To update")}: ${summary.to_update}`}
      />
      <Chip size="small" label={`${t("Skipped")}: ${summary.skipped}`} />
      <Chip
        size="small"
        color={summary.failed > 0 ? "error" : "default"}
        label={`${t("Failed")}: ${summary.failed}`}
      />
    </div>
  );

  return (
    <DashboardAdmin>
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography variant="h4" color="textSecondary">
            {t("Bulk Import Places")}
          </Typography>
        </div>

        <Alert severity="warning" className={classes.warning}>
          <strong>{t("Warning")}:</strong>{" "}
          {t(
            "This tool writes places (pharmacies, laboratories, imaging centers) in bulk. Review the preview before confirming, the operation cannot be undone."
          )}
          <ul>
            <li>
              {t(
                "The file must be an .xlsx with the columns: Number, Province, Zone, Place, Address, Main phone, Additional phone, Email."
              )}
            </li>
            <li>
              {t(
                "The province is matched against the city list; a province with no city creates one."
              )}
            </li>
            <li>
              {t(
                "Places are matched by name. Existing ones are skipped unless you choose to update them."
              )}
            </li>
          </ul>
        </Alert>

        <div className={classes.card}>
          <div className={classes.fileRow}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<GetAppIcon />}
              component={Link}
              to="/files/places_template.xlsx"
              target="_blank"
              download
            >
              {t("Download template")}
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<CloudUploadIcon />}
              onClick={() => hiddenFileInput.current.click()}
              disabled={loading}
            >
              {t("Select Excel file")}
            </Button>

            {file && (
              <Typography variant="body2" color="textSecondary">
                {file.name}
              </Typography>
            )}

            <input
              type="file"
              accept=".xlsx"
              hidden
              ref={hiddenFileInput}
              onChange={handleFileChange}
            />
          </div>

          <div className={classes.fields}>
            <FormControl className={classes.field} variant="outlined">
              <InputLabel id="bulk-type-label">{t("Place type")}</InputLabel>
              <Select
                labelId="bulk-type-label"
                id="bulk-type"
                value={type}
                label={t("Place type")}
                onChange={(e) => {
                  setType(e.target.value);
                  resetPlan();
                }}
              >
                {PLACE_TYPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              className={classes.field}
              variant="outlined"
              label={t("Agreement code")}
              value={agreementCode}
              onChange={(e) => {
                setAgreementCode(e.target.value);
                resetPlan();
              }}
              helperText={t(
                "Optional. The code the patient uses to get the discount. Leave it empty to save it empty."
              )}
              inputProps={{ style: { textTransform: "uppercase" } }}
            />

            <TextField
              className={classes.field}
              variant="outlined"
              label={t("Sheet")}
              value={sheet}
              onChange={(e) => {
                setSheet(e.target.value);
                resetPlan();
              }}
              helperText={t("Optional. Leave it empty to use the first sheet.")}
            />

            <FormControl className={classes.field} variant="outlined">
              <InputLabel id="bulk-existing-label">
                {t("Existing places")}
              </InputLabel>
              <Select
                labelId="bulk-existing-label"
                id="bulk-existing"
                value={onExisting}
                label={t("Existing places")}
                onChange={(e) => {
                  setOnExisting(e.target.value);
                  resetPlan();
                }}
              >
                {ON_EXISTING_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {t("Places are matched by name.")}
              </FormHelperText>
            </FormControl>
          </div>

          <div className={classes.actions}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PreviewIcon />}
              onClick={handlePreview}
              disabled={loading || !file}
            >
              {t("Preview")}
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<CloudUploadIcon />}
              onClick={handleExecute}
              disabled={loading || !file || !plan}
            >
              {t("Import")}
            </Button>

            {loading && <CircularProgress size={24} />}
          </div>
        </div>

        {plan && (
          <div className={classes.card}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {t("Preview")}
            </Typography>

            {renderSummaryChips(plan.summary)}

            {plan.cities_to_create && plan.cities_to_create.length > 0 && (
              <Alert severity="info" style={{ marginTop: 16 }}>
                {t("These cities will be created")}:{" "}
                {plan.cities_to_create.join(", ")}
              </Alert>
            )}

            <TableContainer
              component={Paper}
              className={classes.tableContainer}
            >
              <Table className={classes.table}>
                <TableHead>
                  <TableRow className={classes.tableHeader}>
                    <TableCell>#</TableCell>
                    <TableCell>{t("Place")}</TableCell>
                    <TableCell>{t("City")}</TableCell>
                    <TableCell>{t("Email")}</TableCell>
                    <TableCell>{t("Phone")}</TableCell>
                    <TableCell>{t("Agreement code")}</TableCell>
                    <TableCell>{t("Action")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.rows.map((row, index) => {
                    const meta = ACTION_META[row.action] || ACTION_META.skip;
                    const ActionIcon = meta.icon;
                    return (
                      <TableRow key={`${row.numero}-${index}`} hover>
                        <TableCell>{row.numero}</TableCell>
                        <TableCell>
                          {row.sede}
                          {row.errors.length > 0 && (
                            <div className={classes.errorText}>
                              {row.errors.join("; ")}
                            </div>
                          )}
                          {row.notes.length > 0 && (
                            <div className={classes.noteText}>
                              {row.notes.join(" | ")}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{row.city}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.phone}</TableCell>
                        <TableCell>{row.agreement_code}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={meta.color}
                            icon={<ActionIcon />}
                            label={t(row.action)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
      </div>
    </DashboardAdmin>
  );
}
